import { useState, useMemo, useCallback, useEffect } from "react";
import { RefreshCw, Download, ChevronLeft, ChevronRight, Target, Info, TrendingDown, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import axios from "axios";
import { CollapsibleSection } from "@/components/CollapsibleSection";
import PriorityQueuePanel from "@/components/PriorityQueuePanel";
import { useDisplay } from "@/context/DisplayContext";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const fmt = (v) => {
  if (!v || v === 0) return "-";
  return `£${Number(v).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

function exportPlanCSV(debtGroups, months, monthIndices) {
  const lines = ["Debt,Row," + monthIndices.map(i => months[i] || `M${i+1}`).join(",")];
  Object.entries(debtGroups).forEach(([name, data]) => {
    lines.push(`${name},Scheduled Min,` + monthIndices.map(i => data[i]?.scheduled_min || 0).join(","));
    lines.push(`${name},Actual Paid,` + monthIndices.map(i => data[i]?.actual_payment || 0).join(","));
    lines.push(`${name},Balance,` + monthIndices.map(i => data[i]?.balance_remaining || 0).join(","));
    lines.push(`${name},Interest,` + monthIndices.map(i => data[i]?.interest_amount || 0).join(","));
  });
  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = "the-vault-payoff-plan.csv"; a.click();
  URL.revokeObjectURL(url);
}

function DebtTable({ monthData, debt, visibleMonths, months, editingCell, editValue, setEditValue, setEditingCell, handleCellClick, handleCellSave, handleCellKeyDown, paidOffMonth }) {
  const isPercentage = debt?.min_payment_type === "percentage";
  return (
    <div className="overflow-x-auto custom-scroll">
      <table className="spreadsheet-table w-max">
        <thead>
          <tr>
            <th className="sticky-col text-left" style={{ minWidth: "110px" }}>Row</th>
            {visibleMonths.map((i) => {
              const d = monthData[i];
              const isFinish = paidOffMonth >= 0 && i === paidOffMonth;
              return (
                <th key={i} className={`text-center ${d?.is_promo_expiry ? "bg-amber-100" : ""}`}
                  style={{ minWidth: "88px", borderLeft: isFinish ? "3px dashed #16a34a" : undefined }}>
                  {months[i] || `M${i+1}`}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          <tr className="payoff-row-sched">
            <td className="sticky-col font-medium text-xs text-gray-600 uppercase tracking-wider bg-gray-50/80">
              <span className="flex items-center gap-1">
                {isPercentage && <TrendingDown className="w-3 h-3 text-gray-400" strokeWidth={1.5} />}
                Sched. Min
                {isPercentage && (
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-3 h-3 text-gray-400 cursor-help shrink-0" strokeWidth={1.5} />
                      </TooltipTrigger>
                      <TooltipContent side="right" className="text-xs max-w-[200px]">
                        Calculated at {debt.min_payment_pct}% of previous month's balance (£{debt.min_payment_floor} floor)
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </span>
            </td>
            {visibleMonths.map((i) => {
              const d = monthData[i];
              const isPast = paidOffMonth >= 0 && i > paidOffMonth;
              return <td key={i} className={`numeric text-xs ${isPast ? "text-gray-300 bg-gray-50/50" : ""} ${d?.is_promo_expiry ? "bg-amber-50 interest-cliff-col" : ""}`} style={{ borderLeft: paidOffMonth >= 0 && i === paidOffMonth ? "3px dashed #16a34a" : undefined }}>{d && !isPast ? fmt(d.scheduled_min) : "-"}</td>;
            })}
          </tr>
          <tr className="payoff-row-actual">
            <td className="sticky-col font-medium text-xs text-blue-700 uppercase tracking-wider bg-blue-50/50">Actual Paid</td>
            {visibleMonths.map((i) => {
              const d = monthData[i];
              const isPast = paidOffMonth >= 0 && i > paidOffMonth;
              if (!d || isPast) return <td key={i} className={`numeric text-xs text-blue-700 ${isPast ? "bg-gray-50/50 text-gray-300" : "bg-blue-50/30"}`} style={{ borderLeft: paidOffMonth >= 0 && i === paidOffMonth ? "3px dashed #16a34a" : undefined }}>-</td>;
              const isEditing = editingCell === d.id;
              return (
                <td key={i} className={`text-xs text-blue-700 ${d.is_promo_expiry ? "bg-amber-50 interest-cliff-col" : "bg-blue-50/30"} ${!isEditing ? "numeric editable-cell" : ""}`}
                  style={{ borderLeft: paidOffMonth >= 0 && i === paidOffMonth ? "3px dashed #16a34a" : undefined }}
                  onClick={() => !isEditing && handleCellClick(d.id, d.actual_payment)}>
                  {isEditing ? (
                    <Input data-testid={`edit-actual-${d.id}`} type="number" step="0.01" value={editValue}
                      onChange={(e) => setEditValue(e.target.value)} onBlur={() => handleCellSave(d.id)}
                      onKeyDown={(e) => handleCellKeyDown(e, d.id)}
                      className="h-6 w-20 text-xs font-mono-data p-1 rounded-sm border-blue-300" autoFocus />
                  ) : (
                    <span className="flex items-center justify-end gap-0.5">
                      {fmt(d.actual_payment)}
                      {d.budget_linked && <span className="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" title="Linked from Budget" />}
                    </span>
                  )}
                </td>
              );
            })}
          </tr>
          <tr className="payoff-row-balance">
            <td className="sticky-col font-medium text-xs text-green-700 uppercase tracking-wider bg-green-50/50">Balance</td>
            {visibleMonths.map((i) => {
              const d = monthData[i];
              const isPast = paidOffMonth >= 0 && i > paidOffMonth;
              let bg = isPast ? "bg-gray-50/50" : "bg-green-50/30";
              if (d?.is_promo_expiry) bg = "bg-amber-50";
              else if (d?.is_interest_running && !isPast) bg = "bg-orange-50/30";
              return <td key={i} className={`numeric text-xs font-semibold ${bg} ${isPast ? "text-gray-300" : d?.is_interest_running ? "text-orange-700" : "text-green-700"}`} style={{ borderLeft: paidOffMonth >= 0 && i === paidOffMonth ? "3px dashed #16a34a" : undefined }}>{d && !isPast ? fmt(d.balance_remaining) : isPast ? "-" : "-"}</td>;
            })}
          </tr>
          <tr className="payoff-row-interest">
            <td className="sticky-col font-medium text-xs text-gray-500 uppercase tracking-wider">Interest</td>
            {visibleMonths.map((i) => {
              const d = monthData[i];
              const isPast = paidOffMonth >= 0 && i > paidOffMonth;
              return <td key={i} className={`numeric text-xs ${isPast ? "text-gray-300 bg-gray-50/50" : d?.is_interest_running ? "text-orange-600 bg-orange-50/20" : "text-gray-400"}`} style={{ borderLeft: paidOffMonth >= 0 && i === paidOffMonth ? "3px dashed #16a34a" : undefined }}>{d && !isPast ? fmt(d.interest_amount) : "-"}</td>;
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function SortableDebtBlock({ id, debtName, debt, sectionId, tint, paidOff, earlyFinish, months, monthData, visibleMonths, totalMonthsInPlan, editingCell, editValue, setEditValue, setEditingCell, handleCellClick, handleCellSave, handleCellKeyDown, fmt }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  const headerRight = debt ? (
    <>
      <span className="text-gray-500">Balance: <span className="font-semibold text-gray-700">{fmt(debt.total_balance)}</span></span>
      {debt.min_payment_type === "percentage" && <span className="text-gray-400">Min: {debt.min_payment_pct}%</span>}
      {debt.min_payment_type === "split" && <span className="text-gray-400">Min: Split</span>}
      {earlyFinish && (
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-300 font-semibold uppercase">
          Cleared {months[paidOff] || `M${paidOff+1}`}
        </span>
      )}
      {debt.promo_until && (
        <span className={`${new Date(debt.promo_until) < new Date() ? "text-red-600 font-semibold" : "text-amber-600"}`}>
          0% {new Date(debt.promo_until) < new Date() ? "Expired" : "Until"}: {debt.promo_until}
        </span>
      )}
    </>
  ) : null;

  return (
    <div ref={setNodeRef} style={style} data-testid={`payoff-section-${debtName}`}>
      <CollapsibleSection id={sectionId} title={
        <span className="flex items-center gap-2">
          <span {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing" data-testid={`drag-payoff-${debtName}`}>
            <GripVertical className="w-3.5 h-3.5 text-gray-400" strokeWidth={1.5} />
          </span>
          {debtName}
        </span>
      } headerRight={headerRight} tintClass={tint}>
        {debt && (
          <div className="px-4 py-2 border-b border-gray-200 bg-gray-50/50">
            <span className="text-xs text-gray-500 font-mono-data">
              Opening: {fmt(debt.total_balance)} | APR: {debt.apr_now} | After: {debt.apr_after} | Min: {fmt(debt.statement_min)}
              {debt.promo_until && ` | 0% until: ${debt.promo_until}`}
            </span>
          </div>
        )}
        <DebtTable monthData={monthData} debt={debt} visibleMonths={visibleMonths} months={months}
          editingCell={editingCell} editValue={editValue} setEditValue={setEditValue} setEditingCell={setEditingCell}
          handleCellClick={handleCellClick} handleCellSave={handleCellSave} handleCellKeyDown={handleCellKeyDown}
          paidOffMonth={paidOff} />
      </CollapsibleSection>
    </div>
  );
}

export default function PayoffPlanPage({ payoffPlan, debts, months, onRefresh, api }) {
  const [generating, setGenerating] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState("");
  const { collapseAll, expandAll, isSectionCollapsed } = useDisplay();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  // Plan config state
  const [planMonths, setPlanMonths] = useState(36);
  const [strategy, setStrategy] = useState("avalanche");
  const [extraMonthly, setExtraMonthly] = useState(0);
  const [fixedBudget, setFixedBudget] = useState(false);
  const [selectedYear, setSelectedYear] = useState(0);
  const [planMeta, setPlanMeta] = useState(null);
  const [surplusDismissed, setSurplusDismissed] = useState(false);
  const [customExtra, setCustomExtra] = useState("");
  const [showCustomInput, setShowCustomInput] = useState(false);
  // Increment whenever the plan is (re)generated so the Priority Queue
  // panel re-fetches itself.
  const [priorityRefreshKey, setPriorityRefreshKey] = useState(0);

  // Load config on mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const res = await axios.get(`${api}/payoff-plan/config`);
        if (res.data) {
          setPlanMonths(res.data.months || 36);
          setStrategy(res.data.strategy || "avalanche");
          setExtraMonthly(res.data.extra_monthly || 0);
          setFixedBudget(res.data.fixed_budget || false);
        }
      } catch {}
    };
    loadConfig();
  }, [api]);

  const generatePlan = async () => {
    setGenerating(true);
    setSurplusDismissed(false);
    try {
      const res = await axios.post(`${api}/payoff-plan/generate`, { months: planMonths, strategy, extra_monthly: extraMonthly, fixed_budget: fixedBudget });
      setPlanMeta(res.data);
      setPriorityRefreshKey((k) => k + 1);
      toast.success(`Plan generated: ${planMonths} months, ${strategy}${fixedBudget ? " (fixed)" : ""}`);
      await onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to generate plan"); }
    setGenerating(false);
  };

  const debtGroups = useMemo(() => {
    const groups = {};
    payoffPlan.forEach((p) => { if (!groups[p.debt_name]) groups[p.debt_name] = {}; groups[p.debt_name][p.month_index] = p; });
    return groups;
  }, [payoffPlan]);

  // Use debts sort_order to determine display order (not alphabetical from payoff data)
  const debtNameList = useMemo(() => {
    const namesFromPlan = Object.keys(debtGroups);
    // Order by debts array (which is sorted by sort_order from backend)
    const ordered = debts.filter(d => namesFromPlan.includes(d.name)).map(d => d.name);
    // Add any names from plan that aren't in debts (shouldn't happen but safety)
    namesFromPlan.forEach(n => { if (!ordered.includes(n)) ordered.push(n); });
    return ordered;
  }, [debtGroups, debts]);
  const totalMonthsInPlan = payoffPlan.length > 0 ? Math.max(...payoffPlan.map(p => p.month_index)) + 1 : planMonths;
  const allMonthIndices = Array.from({ length: totalMonthsInPlan }, (_, i) => i);

  // Year tabs
  const yearCount = Math.ceil(totalMonthsInPlan / 12);
  const years = Array.from({ length: yearCount }, (_, i) => i);
  const visibleMonths = useMemo(() => {
    const start = selectedYear * 12;
    const end = Math.min(start + 12, totalMonthsInPlan);
    return Array.from({ length: end - start }, (_, i) => start + i);
  }, [selectedYear, totalMonthsInPlan]);

  // Section IDs
  const sectionIds = useMemo(() => debtNameList.map(n => `payoff-${n.replace(/\s+/g, "-").toLowerCase()}`), [debtNameList]);
  const allCollapsed = sectionIds.length > 0 && sectionIds.every(id => isSectionCollapsed(id));
  const handleToggleAll = () => { if (allCollapsed) expandAll(sectionIds); else collapseAll(sectionIds); };

  // Per-debt paid off month (from planMeta or from data)
  const debtPaidOffMap = useMemo(() => {
    const map = {};
    if (planMeta?.per_debt_payoff) {
      planMeta.per_debt_payoff.forEach(p => { map[p.name] = p.paid_off_month; });
    } else {
      debtNameList.forEach(name => {
        const data = debtGroups[name];
        for (let i = 0; i < totalMonthsInPlan; i++) {
          if (data[i] && data[i].balance_remaining <= 0.01) { map[name] = i; break; }
        }
        if (!(name in map)) map[name] = -1;
      });
    }
    return map;
  }, [planMeta, debtGroups, debtNameList, totalMonthsInPlan]);

  // Goal tracker
  const goalInfo = useMemo(() => {
    if (payoffPlan.length === 0 || debtNameList.length === 0) return null;
    for (let i = 0; i < totalMonthsInPlan; i++) {
      const totalBal = debtNameList.reduce((s, n) => s + (debtGroups[n][i]?.balance_remaining || 0), 0);
      if (totalBal <= 0.01) return { monthIndex: i, monthLabel: months[i] || `M${i + 1}` };
    }
    const lastIdx = totalMonthsInPlan - 1;
    const finalBal = debtNameList.reduce((s, n) => s + (debtGroups[n][lastIdx]?.balance_remaining || 0), 0);
    return { monthIndex: -1, remaining: finalBal };
  }, [payoffPlan, debtGroups, debtNameList, months, totalMonthsInPlan]);

  // Capacity indicator
  const capacityInfo = useMemo(() => {
    const totalMins = debts.reduce((s, d) => {
      if (d.min_payment_type === "percentage" && d.min_payment_pct > 0) {
        return s + Math.max(d.total_balance * d.min_payment_pct / 100, d.min_payment_floor || 5);
      }
      return s + d.statement_min;
    }, 0);
    const required = totalMins + extraMonthly;
    return { totalMins: Math.round(totalMins * 100) / 100, required: Math.round(required * 100) / 100 };
  }, [debts, extraMonthly]);

  // Surplus alert: detect month-over-month minimum drops
  const surplusAlert = useMemo(() => {
    if (payoffPlan.length === 0 || debtNameList.length === 0) return null;
    // Find first month where total mins drop vs previous
    for (let i = 1; i < totalMonthsInPlan; i++) {
      const prevMin = debtNameList.reduce((s, n) => s + (debtGroups[n][i-1]?.scheduled_min || 0), 0);
      const currMin = debtNameList.reduce((s, n) => s + (debtGroups[n][i]?.scheduled_min || 0), 0);
      const drop = prevMin - currMin;
      if (drop > 0.5) {
        // Find priority debt for surplus
        const maxAprDebt = debts.reduce((best, d) => {
          const apr = parseFloat(d.apr_after?.replace("% APR","").replace("%","") || "0");
          const bestApr = parseFloat(best.apr_after?.replace("% APR","").replace("%","") || "0");
          return apr > bestApr ? d : best;
        }, debts[0]);
        // Estimate interest savings over remaining months
        const remainingMonths = totalMonthsInPlan - i;
        const apr = parseFloat(maxAprDebt?.apr_after?.replace("% APR","").replace("%","") || "0");
        const savings = Math.round(drop * remainingMonths * (apr / 100 / 12) * remainingMonths / 2 * 100) / 100;
        return { monthIndex: i, monthLabel: months[i] || `M${i+1}`, drop: Math.round(drop * 100) / 100, targetDebt: maxAprDebt?.name || "highest APR debt", savings: Math.max(savings, Math.round(drop * remainingMonths * 0.15 * 100) / 100) };
      }
    }
    return null;
  }, [payoffPlan, debtGroups, debtNameList, debts, months, totalMonthsInPlan]);

  const handleCellClick = (recordId, currentValue) => { setEditingCell(recordId); setEditValue(String(currentValue)); };
  const handleCellSave = async (recordId) => {
    const val = parseFloat(editValue);
    if (isNaN(val) || val < 0) { setEditingCell(null); return; }
    try { await axios.put(`${api}/payoff-plan/${recordId}?actual_payment=${val}`); toast.success("Payment updated"); await onRefresh(); }
    catch { toast.error("Failed to update"); }
    setEditingCell(null);
  };
  const handleCellKeyDown = (e, recordId) => { if (e.key === "Enter") handleCellSave(recordId); if (e.key === "Escape") setEditingCell(null); };

  const getDebtTint = (debt) => {
    if (!debt?.promo_until) return undefined;
    const expiry = new Date(debt.promo_until);
    return expiry < new Date() ? "bg-red-50/80 hover:bg-red-100/80" : undefined;
  };

  const handlePayoffDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = debtNameList.indexOf(active.id);
    const newIndex = debtNameList.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    // Build new order of debt IDs matching the new name order
    const newNameOrder = arrayMove([...debtNameList], oldIndex, newIndex);
    const newIdOrder = newNameOrder.map(name => {
      const d = debts.find(debt => debt.name === name);
      return d ? d.id : null;
    }).filter(Boolean);
    try {
      await axios.put(`${api}/debts/reorder`, { order: newIdOrder });
      await onRefresh();
    } catch { toast.error("Failed to reorder"); }
  };

  const startLabel = months[0] || "Apr-26";
  const endLabel = months[planMonths - 1] || `M${planMonths}`;

  return (
    <div data-testid="payoff-plan-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl sm:text-2xl md:text-3xl font-semibold tracking-tight">Flexible Payoff Plan</h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">{startLabel} to {endLabel}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {payoffPlan.length > 0 && (
            <>
              <Button data-testid="toggle-all-sections-btn" variant="outline" onClick={handleToggleAll} className="rounded-sm gap-1.5 text-xs">
                {allCollapsed ? <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.5} /> : <ChevronLeft className="w-3.5 h-3.5" strokeWidth={1.5} />}
                {allCollapsed ? "Expand All" : "Collapse All"}
              </Button>
              <Button data-testid="export-plan-btn" variant="outline" onClick={() => exportPlanCSV(debtGroups, months, allMonthIndices)} className="rounded-sm gap-1.5 text-xs">
                <Download className="w-3.5 h-3.5" strokeWidth={1.5} />Export CSV
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Global Controls */}
      <div className="border border-gray-200 rounded-sm p-5 space-y-4" style={{ background: "var(--surface-alt)" }}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Timeline Slider */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-gray-700">Timeline</h3>
              <span className="font-mono-data text-lg font-bold" style={{ color: "var(--text-primary)" }}>{planMonths} months</span>
            </div>
            <Slider data-testid="timeline-slider" value={[planMonths]} onValueChange={(v) => setPlanMonths(v[0])} min={12} max={72} step={1} className="mt-1" />
            <div className="flex justify-between text-xs text-gray-400 font-mono-data mt-1">
              <span>12m</span><span>24m</span><span>36m</span><span>48m</span><span>60m</span><span>72m</span>
            </div>
          </div>

          {/* Strategy Toggle + Fixed Budget + Generate */}
          <div className="space-y-3">
            <div>
              <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-gray-700 mb-2">Strategy</h3>
              <div className="flex rounded-full overflow-hidden border border-gray-300">
                <button data-testid="strategy-avalanche" onClick={() => setStrategy("avalanche")}
                  className={`flex-1 px-3 py-1.5 text-xs font-medium transition-colors ${strategy === "avalanche" ? "bg-black text-white" : "bg-white text-gray-600 hover:bg-gray-100"}`}>
                  Avalanche
                </button>
                <button data-testid="strategy-snowball" onClick={() => setStrategy("snowball")}
                  className={`flex-1 px-3 py-1.5 text-xs font-medium transition-colors ${strategy === "snowball" ? "bg-black text-white" : "bg-white text-gray-600 hover:bg-gray-100"}`}>
                  Snowball
                </button>
              </div>
            </div>
            <button data-testid="fixed-budget-toggle" onClick={() => setFixedBudget(!fixedBudget)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-sm border text-xs font-medium transition-colors ${fixedBudget ? "border-green-400 bg-green-50 text-green-700" : "border-gray-300 bg-white text-gray-500 hover:bg-gray-50"}`}>
              <span>Fixed Monthly Payment</span>
              <span className={`w-8 h-4 rounded-full relative transition-colors ${fixedBudget ? "bg-green-500" : "bg-gray-300"}`}>
                <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform shadow-sm ${fixedBudget ? "translate-x-4" : "translate-x-0.5"}`} />
              </span>
            </button>
            <Button data-testid="generate-plan-btn" onClick={generatePlan} disabled={generating} className="rounded-sm bg-black text-white hover:bg-gray-800 gap-2 w-full">
              <RefreshCw className={`w-4 h-4 ${generating ? "animate-spin" : ""}`} strokeWidth={1.5} />
              {generating ? "Generating..." : "Generate Plan"}
            </Button>
          </div>
        </div>

        {/* Extra Monthly + Capacity */}
        <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-gray-200">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-500 uppercase font-semibold shrink-0">Extra /mo:</span>
            <div className="flex gap-1 flex-wrap">
              {[0, 50, 100, 200, 500].map(amt => {
                const isPreset = !showCustomInput && extraMonthly === amt;
                return (
                  <button key={amt} data-testid={`extra-${amt}`} onClick={() => { setExtraMonthly(amt); setShowCustomInput(false); setCustomExtra(""); }}
                    className={`px-2 py-1 text-xs rounded-full border transition-colors font-mono-data ${isPreset ? "bg-black text-white border-black" : "border-gray-300 hover:bg-gray-100 text-gray-600"}`}>
                    {amt === 0 ? "Min only" : `+£${amt}`}
                  </button>
                );
              })}
              {showCustomInput ? (
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-500">£</span>
                  <input data-testid="extra-custom-input" type="number" min="0" step="1" value={customExtra}
                    onChange={(e) => setCustomExtra(e.target.value)}
                    onBlur={() => { const v = parseInt(customExtra); if (!isNaN(v) && v >= 0) setExtraMonthly(v); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { const v = parseInt(customExtra); if (!isNaN(v) && v >= 0) { setExtraMonthly(v); } } }}
                    className="w-16 h-7 text-xs font-mono-data border border-gray-300 rounded-full px-2 text-center focus:border-black focus:outline-none"
                    autoFocus />
                </div>
              ) : (
                <button data-testid="extra-custom-btn" onClick={() => { setShowCustomInput(true); setCustomExtra(String(![0,50,100,200,500].includes(extraMonthly) ? extraMonthly : "")); }}
                  className={`px-2 py-1 text-xs rounded-full border transition-colors font-mono-data ${showCustomInput || ![0,50,100,200,500].includes(extraMonthly) ? "bg-black text-white border-black" : "border-gray-300 hover:bg-gray-100 text-gray-600"}`}>
                  {![0,50,100,200,500].includes(extraMonthly) ? `+£${extraMonthly}` : "Custom"}
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-xs text-gray-500 uppercase font-semibold">Required:</span>
            <span data-testid="capacity-required" className="font-mono-data text-sm font-bold" style={{ color: "var(--text-primary)" }}>{fmt(capacityInfo.required)}/mo</span>
            {extraMonthly > 0 && (
              <span className="text-xs font-mono-data text-amber-600">(+{fmt(extraMonthly)} from minimums)</span>
            )}
          </div>
        </div>
      </div>

      {/* Goal Tracker */}
      {goalInfo && (
        <div data-testid="goal-tracker" className="border border-gray-200 rounded-sm p-4 flex items-center gap-4" style={{ background: "var(--surface-alt)" }}>
          <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: goalInfo.monthIndex >= 0 ? "var(--chart-positive)" : "var(--border-strong)" }}>
            <Target className="w-5 h-5" style={{ color: goalInfo.monthIndex >= 0 ? "#fff" : "var(--text-muted)" }} strokeWidth={2} />
          </div>
          <div>
            {goalInfo.monthIndex >= 0 ? (
              <>
                <p className="font-heading text-sm font-semibold tracking-tight" style={{ color: "var(--chart-positive)" }}>Debt-Free by {goalInfo.monthLabel}</p>
                <p className="text-xs" style={{ color: "var(--text-secondary)" }}>Total balance reaches £0 in month {goalInfo.monthIndex + 1} of {totalMonthsInPlan} ({strategy})</p>
              </>
            ) : (
              <>
                <p className="font-heading text-sm font-semibold tracking-tight" style={{ color: "var(--text-primary)" }}>{fmt(goalInfo.remaining)} remaining after {totalMonthsInPlan} months</p>
                <p className="text-xs" style={{ color: "var(--text-secondary)" }}>Increase extra payments or extend timeline to reach £0</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Avalanche / Snowball Priority Queue */}
      <PriorityQueuePanel api={api} strategy={strategy} extraMonthly={extraMonthly} refreshKey={priorityRefreshKey} />

      {/* Surplus Alert */}
      {surplusAlert && !surplusDismissed && payoffPlan.length > 0 && (
        <div data-testid="surplus-alert" className="border border-green-300 rounded-sm p-4 flex items-center gap-4" style={{ background: "#F0FDF4" }}>
          <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-green-100 border border-green-300">
            <TrendingDown className="w-5 h-5 text-green-600" strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-green-800">
              Minimums drop by {fmt(surplusAlert.drop)} at {surplusAlert.monthLabel}
            </p>
            <p className="text-xs text-green-600 mt-0.5">
              Redirect to {surplusAlert.targetDebt} and save ~{fmt(surplusAlert.savings)} in total interest
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button data-testid="surplus-apply-btn" onClick={() => { setFixedBudget(true); setSurplusDismissed(true); toast.success("Fixed budget enabled — surplus locked!"); }}
              className="rounded-sm bg-green-600 text-white hover:bg-green-700 text-xs px-3 py-1 h-auto">
              Apply to Plan
            </Button>
            <button data-testid="surplus-dismiss-btn" onClick={() => setSurplusDismissed(true)} className="text-xs text-green-500 hover:text-green-700">Dismiss</button>
          </div>
        </div>
      )}

      {/* Year Pill Tabs */}
      {payoffPlan.length > 0 && yearCount > 1 && (
        <div className="flex gap-1.5 flex-wrap" data-testid="year-tabs">
          {years.map(y => {
            const start = y * 12;
            const yearLabel = months[start] ? months[start].split("-")[1] : "";
            return (
              <button key={y} data-testid={`year-tab-${y}`} onClick={() => setSelectedYear(y)}
                className={`px-4 py-1.5 text-xs font-medium transition-colors ${selectedYear === y ? "bg-black text-white" : "border border-gray-300 text-gray-600 hover:bg-gray-100"}`}
                style={{ borderRadius: "999px" }}>
                Year {y + 1} {yearLabel && <span className="text-gray-400 ml-1">'{yearLabel}</span>}
              </button>
            );
          })}
        </div>
      )}

      {/* Color Legend */}
      <div className="flex flex-wrap gap-4 text-xs border border-gray-200 rounded-sm p-2 bg-gray-50">
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-gray-200 border border-gray-300 rounded-sm inline-block"></span> Scheduled Min</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-blue-100 border border-blue-300 rounded-sm inline-block"></span> Actual Payment (editable)</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-100 border border-green-300 rounded-sm inline-block"></span> Balance Remaining</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-amber-100 border border-amber-300 rounded-sm inline-block"></span> 0% Expires</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-orange-100 border border-orange-300 rounded-sm inline-block"></span> Interest Running</span>
        <span className="flex items-center gap-1"><span className="w-4 h-3 border-l-2 border-dashed border-green-600 inline-block"></span> Debt Cleared</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span> Linked from Budget</span>
      </div>

      {payoffPlan.length === 0 ? (
        <div className="border border-gray-200 rounded-sm p-12 text-center">
          <p className="text-gray-400 text-sm">No payoff plan generated yet.</p>
          <p className="text-gray-400 text-xs mt-1">Configure your timeline above and click "Generate Plan".</p>
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handlePayoffDragEnd}>
        <SortableContext items={debtNameList} strategy={verticalListSortingStrategy}>
        <div className="space-y-3">
          {debtNameList.map((debtName, idx) => {
            const monthData = debtGroups[debtName];
            const debt = debts.find((d) => d.name === debtName);
            const sectionId = sectionIds[idx];
            const tint = getDebtTint(debt);
            const paidOff = debtPaidOffMap[debtName] ?? -1;
            const earlyFinish = paidOff >= 0 && paidOff < totalMonthsInPlan - 1;

            return (
              <SortableDebtBlock key={debtName} id={debtName} debtName={debtName} debt={debt} sectionId={sectionId} tint={tint} paidOff={paidOff} earlyFinish={earlyFinish} months={months} monthData={monthData} visibleMonths={visibleMonths} totalMonthsInPlan={totalMonthsInPlan}
                editingCell={editingCell} editValue={editValue} setEditValue={setEditValue} setEditingCell={setEditingCell} handleCellClick={handleCellClick} handleCellSave={handleCellSave} handleCellKeyDown={handleCellKeyDown} fmt={fmt} />
            );
          })}

          {/* Total Debt Row */}
          <div className="border border-gray-200 rounded-sm bg-gray-50">
            <div className="px-4 py-3 border-b border-gray-200"><h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-gray-700">TOTAL DEBT</h3></div>
            <div className="overflow-x-auto custom-scroll">
              <table className="spreadsheet-table w-max">
                <thead><tr><th className="sticky-col text-left" style={{ minWidth: "110px" }}>Row</th>{visibleMonths.map((i) => <th key={i} className="text-center" style={{ minWidth: "88px" }}>{months[i] || `M${i+1}`}</th>)}</tr></thead>
                <tbody>
                  <tr className="font-semibold">
                    <td className="sticky-col font-medium text-xs uppercase tracking-wider bg-gray-50">Total Min</td>
                    {visibleMonths.map((i) => { const total = debtNameList.reduce((s, n) => s + (debtGroups[n][i]?.scheduled_min || 0), 0); return <td key={i} className="numeric text-xs">{fmt(total)}</td>; })}
                  </tr>
                  <tr className="font-semibold bg-green-50/30">
                    <td className="sticky-col font-medium text-xs text-green-700 uppercase tracking-wider bg-green-50/50">Total Bal</td>
                    {visibleMonths.map((i) => { const total = debtNameList.reduce((s, n) => s + (debtGroups[n][i]?.balance_remaining || 0), 0); return <td key={i} className="numeric text-xs text-green-700">{fmt(total)}</td>; })}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        </SortableContext>
        </DndContext>
      )}
    </div>
  );
}
